import {
  Box,
  Col,
  css,
  cx,
  DSButton,
  DSIconButton,
  DSIconMinus,
  DSIconPlus,
  DSIconSearch,
  Row,
  Text,
} from "@cdx/ds";
import {makeScrollable} from "@cdx/ds/utils/makeScrollable";
import {RevealSpinner, useEsc} from "@cdx/common";
import {searchStyles as styles} from "../search/search.css";
import {colorThemes} from "@cdx/ds/css/themes/color-overwrites.css";
import {SearchInputActions, useSearchInput} from "../search/useSearchInput";
import {useOrderedSelectedProjects} from "../../lib/hooks/useSelectedProjects";
import {checkIfPresent, useRoot} from "../../lib/mate/mate-utils";
import {KeyboardEventHandler, ReactNode, useRef, useState} from "react";
import {getSearchLabel, SearchHint, SuggestionBlock} from "../search/SearchOverlay";
import {SearchFilter} from "../search/search-types";
import ActiveSearchFilters from "../search/ActiveSearchFilters";
import {Root} from "../../cdx-models/Root";
import {filterToKey} from "../../lib/search-utils";
import {
  SearchQuery,
  VisionBoardNode,
  VisionBoardNodeId,
} from "../../cdx-models/utils/extended-fields";
import {VisionBoardBag} from "./canvas-utils";
import {Card, CardId} from "../../cdx-models/Card";
import {CARD_ROW_HEIGHT} from "../card-panel/TableView";
import {useVirtualizer, VirtualItem} from "@tanstack/react-virtual";
import {tableViewStyles} from "../card-panel/card-panel.css";
import {getStatusForCard} from "../../lib/card-status-utils";
import {api} from "../../lib/api";
import {OwnerCell, StatusRect, TableCell, TitleCell} from "../card-panel/TableViewCells";
import {cardStatusVariants} from "@cdx/ds/components/DSCard/DSCardTheme.css";
import {pluralize} from "../../lib/utils";
import {useServerData} from "../../lib/request";
import {VisionBoardQueryId} from "../../cdx-models/VisionBoardQuery";
import {waitForResultPromise} from "../../lib/wait-for-result";

const SearchBar = ({
  searchInputActions,
  value,
  filters,
  onRemoveFilter,
  root,
  onSave,
  saveButtonLabel,
}: {
  searchInputActions: SearchInputActions;
  value: string;
  filters: SearchFilter[];
  onRemoveFilter: (filter: SearchFilter) => void;
  root: Root;
  onSave: () => void;
  saveButtonLabel: string | null;
}) => {
  const [isFocussed, setIsFocussed] = useState(false);
  const isActive = false;

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.which === 38) {
      // UP
      searchInputActions.selectAbove();
      e.preventDefault();
    } else if (e.which === 40) {
      // DOWN
      searchInputActions.selectBelow();
      e.preventDefault();
    } else if (e.which === 13) {
      // RETURN
      searchInputActions.selectIndexed();
      e.preventDefault();
    } else if (e.which === 8 && !value) {
      // BACKSPACE
      if (filters.length > 0) {
        onRemoveFilter(filters[filters.length - 1]);
        e.preventDefault();
      }
      // } else if (forceOr) {
      //   searchActions.toggleForceOr();
      // } else if (showIncludeArchivedCards) {
      //   searchActions.toggleArchived();
      //   e.preventDefault();
      // }
    }
  };

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Col sp="12px">
      <Row
        className={cx(
          styles.container.default,
          styles.container.light,
          isFocussed && styles.container.lightFocussed
        )}
        px="12px"
        sp="12px"
        align="center"
        height="32px"
      >
        <Row as="label" align="center" sp="12px" flex="auto">
          {!isActive && (
            <DSIconSearch
              size={20}
              className={css({color: "primary"}, colorThemes.purpleTextOnDark)}
            />
          )}
          <input
            type="text"
            value={value}
            onChange={searchInputActions.onChangeSearchInput}
            onKeyDown={handleKeyDown}
            className={cx(styles.input.default, styles.input.focussed)}
            placeholder="Add filter..."
            onFocus={() => setIsFocussed(true)}
            onBlur={() => setIsFocussed(false)}
            ref={inputRef}
            autoFocus
          />
        </Row>
      </Row>
      <Row sp="12px" align="start">
        {filters.length > 0 && (
          <ActiveSearchFilters
            filters={filters}
            root={root}
            includeArchivedCards={false}
            forceOr={false}
            onRemoveToken={(filter: SearchFilter) => {
              onRemoveFilter(filter);
              inputRef.current?.focus();
            }}
            wrap
          />
        )}
        {saveButtonLabel && (
          <DSButton className={css({ml: "auto"})} onClick={onSave}>
            {saveButtonLabel}
          </DSButton>
        )}
      </Row>
    </Col>
  );
};

const ItemRow = ({
  virtualRow,
  items,
  root,
  getActionCell,
}: {
  virtualRow: VirtualItem<any>;
  items: {id: CardId; card: Card | null}[];
  root: Root;
  getActionCell?: (cardId: CardId) => ReactNode;
}) => {
  const {card, id} = items[virtualRow.index];
  const {result: status, isLoaded: isStatusLoaded} = checkIfPresent(
    () => (card ? (getStatusForCard(card) as "hero") : "unassigned"),
    api
  );
  const cardContainerKey = "preview";
  return (
    <Row
      style={{
        height: `${virtualRow.size - 1}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
      bg="foreground"
      align="center"
      px="8px"
      colorTheme="white"
      className={cx(
        tableViewStyles.row.base,
        cardStatusVariants[status],
        tableViewStyles.cardRow.base
      )}
    >
      {card ? (
        <>
          <StatusRect status={status} isLoaded={isStatusLoaded} card={card} />
          <OwnerCell card={card} canModify={false} cardContainerKey={cardContainerKey} />
          <TitleCell card={card} root={root} />
          {getActionCell && getActionCell(id)}
        </>
      ) : (
        <>
          <TableCell
            fixed={128}
            flex={2}
            whiteSpace="nowrap"
            textOverflow="clip"
            size={14}
            lineHeight="16px"
            display="flex"
            sp="4px"
            align="baseline"
          >
            <Text type="label12" color="secondary" textAlign="center" flex="auto">
              Inaccessible card
            </Text>
          </TableCell>
          {getActionCell && getActionCell(id)}
        </>
      )}
    </Row>
  );
};

const Table = ({
  items,
  root,
  scrollNode,
  getActionCell,
}: {
  items: {card: null | Card; id: CardId}[];
  root: Root;
  scrollNode: HTMLElement;
  getActionCell?: (id: CardId) => ReactNode;
}) => {
  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollNode,
    estimateSize: () => CARD_ROW_HEIGHT,
    overscan: 5,
    getItemKey: (idx: number) => items[idx].id,
  });
  return (
    <Box
      width="100%"
      relative
      zIndex={1}
      style={{
        minHeight: CARD_ROW_HEIGHT,
        height: `${rowVirtualizer.getTotalSize()}px`,
      }}
      rounded={4}
      colorTheme="gray25"
    >
      {rowVirtualizer.getVirtualItems().map((virtualRow) => (
        <ItemRow
          key={virtualRow.key}
          virtualRow={virtualRow}
          items={items}
          root={root}
          getActionCell={getActionCell}
        />
      ))}
    </Box>
  );
};

const ShowCardIds = ({
  ids,
  root,
  scrollNode,
  onRemoveId,
}: {
  ids: CardId[];
  root: Root;
  scrollNode: HTMLElement;
  onRemoveId: (id: CardId) => void;
}) => {
  return (
    <Col sp="12px" pb="12px">
      <Text type="label12">Selected Cards</Text>
      <Table
        root={root}
        items={ids.map((id) => ({id, card: api.getModel({modelName: "card", id})}))}
        scrollNode={scrollNode}
        getActionCell={(id) => (
          <TableCell fixed={32}>
            <DSIconButton
              size="sm"
              icon={<DSIconMinus />}
              variant="tertiary"
              onClick={() => onRemoveId(id)}
            />
          </TableCell>
        )}
      />
    </Col>
  );
};

const PreviewFilter = ({
  filters,
  root,
  scrollNode,
  selectedIds,
  handleAddCard,
}: {
  filters: SearchFilter[];
  root: Root;
  scrollNode: HTMLElement;
  selectedIds: Set<CardId>;
  handleAddCard: (id: CardId) => void;
}) => {
  const {data: cardIds, isLoaded} = useServerData({
    method: "POST",
    path: "/services/search-cards",
    data: {query: {filters}},
    dataKey: filters,
  });
  const cardCount = cardIds && cardIds.length;

  return (
    <Col sp="12px" pt="12px">
      <RevealSpinner show={!isLoaded} />
      {cardIds && (
        <>
          <Text type="label12">
            {cardCount < 50 ? pluralize(cardCount, "Card") : `Previewing 50 Cards`}
          </Text>
          <Table
            root={root}
            items={cardIds.map((id: CardId) => ({id, card: api.getModel({modelName: "card", id})}))}
            scrollNode={scrollNode}
            getActionCell={(id) => (
              <TableCell fixed={32}>
                <DSIconButton
                  size="sm"
                  icon={<DSIconPlus />}
                  active={selectedIds.has(id)}
                  variant="tertiary"
                  onClick={() => handleAddCard(id)}
                  disabled={selectedIds.has(id)}
                />
              </TableCell>
            )}
          />
        </>
      )}
    </Col>
  );
};

type LoadedProps = {
  onClose: () => void;
  onSave: (nextQuery: SearchQuery) => void;
  initialQuery: SearchQuery;
};
const FilterBarWithLoadedQuery = ({onSave, onClose, initialQuery}: LoadedProps) => {
  const [scrollNode, setScrollNode] = useState<HTMLElement | null>(null);
  const root = useRoot();
  const projects = useOrderedSelectedProjects(root);
  const [explicitIds, setExplicitIds] = useState<CardId[]>(
    initialQuery.type === "cardIds" ? initialQuery.ids : []
  );
  const [filters, setFilters] = useState<SearchFilter[]>(() =>
    initialQuery.type === "filter" ? initialQuery.filters : []
  );

  const handleAddCard = (id: CardId) => {
    setExplicitIds((prev) => [...prev.filter((p) => p !== id), id]);
  };

  const [{searchValue, selectedIndex, suggestions, waitingForLoadedTimeoutId}, searchInputActions] =
    useSearchInput({
      projects,
      filterTasks: (t) => t.category !== "gotoDecks" && t.category !== "gotoAnyDecks",
      onEntrySelect: (sugg, entry) => handleAddCard(entry.value),
      onAdd: (filter) => {
        setFilters((prev) => [...prev, filter]);
      },
    });
  const handleRemoveFilter = (filter: SearchFilter) => {
    setFilters((prev) => prev.filter((f) => filterToKey(f) !== filterToKey(filter)));
  };
  const handleRemoveId = (id: CardId) => {
    setExplicitIds((prev) => prev.filter((i) => i !== id));
  };

  const handleSave = async () => {
    if (explicitIds.length > 0) {
      await onSave({type: "cardIds", ids: explicitIds});
    } else if (filters.length > 0) {
      await onSave({type: "filter", filters});
    }
    onClose();
  };

  const getSaveButtonLabel = () => {
    if (explicitIds.length > 0) {
      return "Save Cards";
    } else if (filters.length > 0) {
      return "Save Query";
    } else {
      return null;
    }
  };

  const waitingForMore = waitingForLoadedTimeoutId !== null;

  let offset = 0;

  const getBottomPart = () => {
    if (suggestions.length) {
      if (waitingForMore) return null;
      return (
        <Col sp="12px" flex="none" pt="12px">
          {suggestions.map((suggestion) => {
            const prevOffset = offset;
            offset += suggestion.entries.length;
            return (
              <SuggestionBlock
                key={suggestion.category}
                selectedIndex={selectedIndex}
                root={root}
                suggestion={suggestion}
                offset={prevOffset}
                searchInputActions={searchInputActions}
              />
            );
          })}
        </Col>
      );
    } else {
      if (filters.length > 0) {
        return (
          scrollNode && (
            <PreviewFilter
              filters={filters}
              root={root}
              scrollNode={scrollNode}
              handleAddCard={handleAddCard}
              selectedIds={new Set(explicitIds)}
            />
          )
        );
      } else if (explicitIds.length === 0) {
        return (
          <Box>
            <SearchHint />
          </Box>
        );
      }
    }
    return null;
  };

  return (
    <Layout>
      <Box pa="12px">
        <SearchBar
          value={searchValue}
          searchInputActions={searchInputActions}
          filters={filters}
          onRemoveFilter={handleRemoveFilter}
          root={root}
          onSave={handleSave}
          saveButtonLabel={getSaveButtonLabel()}
        />
      </Box>
      <Col divideX flex="auto" className={makeScrollable()} pa="12px" ref={setScrollNode}>
        <RevealSpinner show={waitingForMore} />
        {explicitIds.length > 0 && scrollNode && (
          <ShowCardIds
            ids={explicitIds}
            root={root}
            scrollNode={scrollNode}
            onRemoveId={handleRemoveId}
          />
        )}
        {getBottomPart()}
      </Col>
    </Layout>
  );
};

const Layout = ({children}: {children: ReactNode}) => (
  <Col height="100%" colorTheme="gray25" bg="foreground" divideX style={{width: 400}}>
    {children}
  </Col>
);

type FilterNode = VisionBoardNode & {type: "filter"};

const FilterBar = ({bag, id}: {bag: VisionBoardBag; id: VisionBoardNodeId}) => {
  const close = () => bag.showVisionBoardFilter(null);
  useEsc(close);
  const [nodeValue] = useState(() => {
    const n = bag.store.getNode(id);
    if (!n) return null;
    return n.node.get("value") as FilterNode["value"];
  });
  if (!nodeValue) {
    return (
      <Layout>
        <Text type="label12">Node not found</Text>
      </Layout>
    );
  }
  if (nodeValue.filter) {
    return (
      <Layout>
        <Text type="label12">Legacy Widget. Please delete</Text>
      </Layout>
    );
  }

  const getSearchQuery = (): {query: SearchQuery | null; id: VisionBoardQueryId | null} => {
    if (nodeValue.queryId === null) return {query: {filters: [], type: "filter"}, id: null};
    const instance = api.getModel({modelName: "visionBoardQuery", id: nodeValue.queryId});
    if (!instance) return {query: {filters: [], type: "filter"}, id: null};
    return {query: instance.$meta.get("query", null), id: nodeValue.queryId};
  };
  const result = getSearchQuery();
  const cardId = bag.visionBoard.card.cardId;

  const handleSave = async (next: SearchQuery) => {
    const [label, {id: nextId}] = await Promise.all([
      waitForResultPromise(() =>
        next.type === "filter" ? getSearchLabel(next.filters, {root: bag.root}) : null
      ),
      api.mutate.visionBoard.createQuery({
        cardId,
        query: next,
      }),
    ]);
    const n = bag.store.getNode(id);
    if (!n) return;
    n.node.set("value", {...nodeValue, queryId: nextId, label: label ?? nodeValue.label});
    if (result.id) {
      await api.mutate.visionBoard.markQueryStale({id: result.id});
    }
  };

  return (
    <Layout>
      <RevealSpinner show={result.query === null} />
      {result.query ? (
        <FilterBarWithLoadedQuery initialQuery={result.query} onClose={close} onSave={handleSave} />
      ) : null}
    </Layout>
  );
};

export default FilterBar;
